import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loader2, CheckCircle2, XCircle, Bell, Plug, Rocket, Gauge } from "lucide-react";
import { toast } from "sonner";

const FIX = {
  OPENAI_API_KEY: "Add OPENAI_API_KEY to the backend environment and restart.",
  OPENAI_MODEL: "Set OPENAI_MODEL (e.g. gpt-5.6-terra) in the backend environment.",
  OPENAI_IMAGE_MODEL: "Set OPENAI_IMAGE_MODEL=gpt-image-2 in the backend environment.",
  "Persistent media storage": "Configure Emergent object storage (EMERGENT_LLM_KEY).",
  "Super Admin password changed from temporary": "Change the Super Admin password from the temporary one.",
  "Campaign landing page present": "Add a landing-page URL to at least one campaign or offer.",
  ELEVENLABS_API_KEY: "Add ElevenLabs credentials when enabling voice/video (later phase).",
  ELEVENLABS_VOICE_ID: "Set an ElevenLabs voice ID (later phase).",
  ELEVENLABS_MODEL_ID: "Set an ElevenLabs model ID (later phase).",
  BUFFER_CLIENT_ID: "Add Buffer app credentials to enable publishing (later phase).",
  BUFFER_CLIENT_SECRET: "Add the Buffer client secret (later phase).",
  BUFFER_REDIRECT_URI: "Set the Buffer OAuth redirect URI (later phase).",
  "At least one mapped social channel": "Connect and map a Buffer channel (later phase).",
  "Media-generation smoke test passed": "Run a Create Media action to validate the pipeline.",
  "Buffer draft/scheduled test passed": "Test a Buffer draft when publishing is enabled (later phase).",
};

const GROUP_LABELS = {
  required_to_deploy: "Required to deploy",
  required_for_voice_video: "Required for voice / video",
  required_for_automated_publishing: "Required for automated publishing",
  optional: "Optional",
};

function StatusRow({ label, ok, detail }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 text-sm" data-testid={`status-${label.replace(/\W+/g, "-").toLowerCase()}`}>
      <span className="text-gray-700">{label}{detail ? <span className="text-gray-400"> · {detail}</span> : null}</span>
      {ok ? <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Configured</span>
        : <span className="flex items-center gap-1 text-gray-400"><XCircle className="h-4 w-4" /> Not configured</span>}
    </div>
  );
}

export default function Operations() {
  const [conn, setConn] = useState(null);
  const [aiText, setAiText] = useState(null);
  const [imgStatus, setImgStatus] = useState(null);
  const [ready, setReady] = useState(null);
  const [usage, setUsage] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/marketing/media/status").then((r) => setConn(r.data)).catch(() => setConn(false));
    api.get("/marketing/ai/status").then((r) => setAiText(r.data)).catch(() => {});
    api.get("/marketing/media/image-status").then((r) => setImgStatus(r.data)).catch(() => {});
    api.get("/marketing/deployment-readiness").then((r) => setReady(r.data)).catch(() => {});
    api.get("/marketing/media/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/marketing/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const testText = async () => { setBusy(true); try { const r = await api.post("/marketing/ai/test-connection"); toast.success(`Text OK · ${r.data.model}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const testImage = async () => { setBusy(true); try { const r = await api.post("/marketing/media/image-test-connection"); toast.success(`Image OK · ${r.data.model}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const markAllRead = async () => { await api.post("/marketing/notifications/read-all"); load(); };

  if (conn === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;

  return (
    <div data-testid="operations-page" className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl font-bold">Media Operations</h2>
        <p className="text-sm text-gray-500 mt-1">Live connection status, deployment readiness, media usage and internal notifications. Secret values are never displayed.</p>
      </div>

      {/* Connections */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="media-connections">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold flex items-center gap-2"><Plug className="h-4 w-4 text-[#581C87]" /> Media Connections</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={testText} disabled={busy} data-testid="test-text-button">Test Text</Button>
            <Button size="sm" variant="outline" onClick={testImage} disabled={busy} data-testid="test-image-button">Test Image</Button>
          </div>
        </div>
        <div className="mt-3">
          <StatusRow label="Object storage" ok={conn?.storage?.configured} detail={conn?.storage?.provider} />
          <StatusRow label="OpenAI text" ok={aiText?.configured} detail={aiText?.model} />
          <StatusRow label="OpenAI image" ok={imgStatus?.configured} detail={imgStatus?.model} />
          <StatusRow label="ElevenLabs (voice)" ok={conn?.voice?.configured} detail={conn?.voice?.model} />
          <StatusRow label="Buffer (publishing)" ok={conn?.buffer?.configured} />
        </div>
      </div>

      {/* Readiness */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="deployment-readiness">
        <h3 className="font-heading font-bold flex items-center gap-2"><Rocket className="h-4 w-4 text-[#581C87]" /> Deployment Readiness</h3>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-6">
          {ready && Object.entries(ready).map(([group, checks]) => (
            <div key={group} data-testid={`readiness-${group}`}>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{GROUP_LABELS[group] || group}</p>
              {checks.map((c) => (
                <div key={c.key} className="py-1.5 border-b border-gray-50 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{c.key}</span>
                    {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-amber-500" />}
                  </div>
                  {!c.ok && <p className="text-xs text-amber-600 mt-0.5">→ {FIX[c.key] || "Complete this configuration."}</p>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Usage */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="media-usage">
        <h3 className="font-heading font-bold flex items-center gap-2"><Gauge className="h-4 w-4 text-[#581C87]" /> Media Usage & Limits</h3>
        {usage && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[["Image used", `${usage.image_used}/${usage.image_limit}`], ["Remaining", usage.remaining], ["Reserved", usage.image_reserved], ["Image jobs", usage.image_generation_jobs], ["Template renders", usage.template_renders], ["Successful", usage.successful_outputs], ["Failed", usage.failed_outputs]].map(([l, v]) => (
              <div key={l} className="bg-gray-50 rounded-lg p-3"><p className="font-heading text-xl font-bold">{v}</p><p className="text-xs text-gray-500">{l}</p></div>
            ))}
          </div>
        )}
        {usage?.warn && <p className="mt-3 text-sm text-amber-600">You have used 80%+ of the monthly image-generation allowance.</p>}
      </div>

      {/* Notifications */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="notifications-panel">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold flex items-center gap-2"><Bell className="h-4 w-4 text-[#581C87]" /> Notifications {notifs?.unread ? <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs">{notifs.unread} unread</span> : null}</h3>
          <Button size="sm" variant="outline" onClick={markAllRead} data-testid="mark-all-read-button">Mark all read</Button>
        </div>
        <div className="mt-3">
          {(!notifs || notifs.items.length === 0) && <p className="text-sm text-gray-400 py-4">No notifications yet.</p>}
          {notifs?.items?.slice(0, 30).map((n) => (
            <div key={n.id} className={`py-2 border-b border-gray-50 text-sm ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">{n.title}</span>
                <span className="text-xs text-gray-400">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <p className="text-xs text-gray-500">{n.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
